import Image from 'next/image';
import { useTranslations } from 'next-intl';

/**
 * A purely decorative landscape-iPad mockup of the student Rewards screen, used
 * as the floating element in the marketing hero. Mirrors the real gamification
 * dashboard (XP / Level / Streak tiles, an orange course-progress bar, and a
 * badge collection) using brand colors — no live data, no interactivity.
 */
const STAT_TILES = [
  { icon: '⭐', value: '1,250', labelKey: 'heroMockup.xp' },
  { icon: '🔥', value: '12', labelKey: 'heroMockup.dayStreak' },
  { icon: '🏅', value: '8', labelKey: 'heroMockup.badges' },
];

const BADGES = [
  { icon: '🏆', earned: true },
  { icon: '📚', earned: true },
  { icon: '🎯', earned: true },
  { icon: '⚡', earned: true },
  { icon: '🌟', earned: false },
  { icon: '🚀', earned: false },
];

export function HeroAppMockup({ className }: { className?: string }) {
  const t = useTranslations('public');
  return (
    <div className={className} aria-hidden>
      {/* Device bezel — landscape iPad */}
      <div className="relative aspect-[4/3] w-[14.85rem] rounded-[1.4rem] bg-[#23252b] p-2.5 shadow-2xl ring-1 ring-black/10 xl:w-[16.65rem]">
        {/* Front camera on the long-edge bezel */}
        <span className="absolute left-1/2 top-[5px] h-1 w-1 -translate-x-1/2 rounded-full bg-white/30" />

        {/* Screen */}
        <div className="flex h-full w-full flex-col gap-1.5 overflow-hidden rounded-[0.8rem] bg-[#fcf7ee] p-2">
          {/* Top bar */}
          <div className="flex items-center justify-between rounded-md bg-white px-2 py-1 shadow-sm">
            <div className="flex items-center gap-1">
              <Image src="/eifo-logo.png" alt="" width={16} height={16} className="h-4 w-4" />
              <span className="text-[10px] font-bold text-[#1e306e]">{t('heroMockup.myRewards')}</span>
            </div>
            <span className="rounded-full bg-[#fc800a]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#fc800a]">
              {t('heroMockup.level', { level: 7 })}
            </span>
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-3 gap-1.5">
            {STAT_TILES.map((tile) => (
              <div
                key={tile.labelKey}
                className="flex flex-col items-center rounded-md bg-white px-1 py-1.5 shadow-sm"
              >
                <span className="text-sm leading-none">{tile.icon}</span>
                <span className="mt-0.5 text-[11px] font-bold leading-none text-[#1e306e]">
                  {tile.value}
                </span>
                <span className="mt-0.5 text-[7px] uppercase tracking-wide text-[#6b7280]">
                  {t(tile.labelKey)}
                </span>
              </div>
            ))}
          </div>

          {/* Course progress */}
          <div className="rounded-md bg-white px-2 py-1.5 shadow-sm">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[8px] font-medium text-[#1e306e]">{t('heroMockup.courseProgress')}</span>
              <span className="text-[8px] font-semibold text-[#fc800a]">75%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#fcf7ee]">
              <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-[#fc800a] to-[#f59e0b]" />
            </div>
          </div>

          {/* Badge collection */}
          <div className="flex flex-1 flex-col rounded-md bg-white px-2 py-1.5 shadow-sm">
            <span className="text-[8px] font-bold text-[#1e306e]">{t('heroMockup.badgeCollection')}</span>
            <div className="mt-auto flex items-center justify-between">
              {BADGES.map((b, i) => (
                <span
                  key={i}
                  className={
                    b.earned
                      ? 'flex h-6 w-6 items-center justify-center rounded-full bg-[#fc800a]/10 text-xs'
                      : 'flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs opacity-40 grayscale'
                  }
                >
                  {b.icon}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
